import * as React from 'react'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { FieldArrayRenderProps } from 'formik/dist/FieldArray'
import { Alert, Tree } from '@fluentui/react-components/unstable'
import { InvoiceProps } from './InvoiceForm'
import { useFormikContext } from 'formik'
import { FilePartyMetaDataDto, InvoiceMutationDto, InvoiceType } from '../../../../config/finance/invoiceConfig'
import { makeStyles, Toolbar, ToolbarButton, ToolbarGroup, Tooltip } from '@fluentui/react-components'
import { AddRegular, ReOrderDotsVerticalRegular } from '@fluentui/react-icons'
import { css, MessageBar, MessageBarType, Stack, StackItem } from '@fluentui/react'
import { baseListStyles } from '../../../List/BaseList'
import { IFilePartyDropdownOption } from '../../../Dropdown/LegacyFilePartyDropdown'
import { Link } from 'react-router-dom'
import { InvoiceChildContent } from './InvoiceChildContent'

type Props = FieldArrayRenderProps & InvoiceProps

const useStyles = makeStyles({
    toolbar: {
        justifyContent: 'end',
    },
    treeItem: {
        marginLeft: '-16px',
        marginRight: '-16px',
        '&.is-selected': {
            marginLeft: '-16px',
            marginRight: '-16px',
            paddingLeft: '7px',
            paddingRight: '7px',
        },
    },
})

export default function InvoiceChildren(props: Props): ReactElement {
    const styles = useStyles()
    const { values } = useFormikContext<InvoiceMutationDto>()
    const {
        values: { children },
        setFieldValue,
    } = useFormikContext<InvoiceMutationDto>()

    const [filePartyOptions, setFilePartyOptions] = useState<IFilePartyDropdownOption<FilePartyMetaDataDto>[]>()
    useEffect(() => {
        setFilePartyOptions(
            props.meta.data.fileParties.map((fileParty: FilePartyMetaDataDto) => ({
                key: fileParty.id,
                text: fileParty.displayName,
                disabled: !fileParty.hasAddress,
                role: fileParty.role,
                data: fileParty,
            })),
        )
    }, [props.meta.data])

    const [currentPosition, setCurrentPosition] = useState<number>()
    const [nextPosition, setNextPosition] = useState<number>()
    const elements = useRef<HTMLElement[]>([])
    const addCallback = () => {
        if (!children) {
            setFieldValue('children', [])
        }

        props.push({
            ...values,
            number: `${values.number}-${children.length + 1}`,
            name: `${values.name} - ${children.length + 1}`,
            factor: 1,
            type: InvoiceType.SubSplit,
            messageSubject: (props.meta?.data?.emailMessages || [])
                .filter(message => message.language === values.language)
                .find(message => !message.currency || message.currency === values.currency)?.subject,
            messageBody: (props.meta?.data?.emailMessages || [])
                .filter(message => message.language === values.language)
                .find(message => !message.currency || message.currency === values.currency)?.body,
            paymentMessageBody: (props.meta?.data?.paymentMessages || [])
                .filter(message => message.language === values.language)
                .find(message => !message.currency || message.currency === values.currency)?.body,
            //cleanup subinvoice
            children: [],
            lineItemGroups: [],
        })
    }

    useEffect(() => {
        // Make sure that the refs are always kept updated.
        // Courtesy of https://stackoverflow.com/a/56063129
        if (children) {
            elements.current = elements.current.slice(0, children.length)
        }
    }, [children])

    useEffect(() => {
        if (nextPosition !== undefined && nextPosition !== currentPosition) {
            props.move(currentPosition, nextPosition)
            setCurrentPosition(nextPosition)
            setNextPosition(undefined)
        }
    }, [nextPosition])

    if (!props.meta.loading && props.meta.data.fileParties.length === 0) {
        return (
            <MessageBar messageBarType={MessageBarType.blocked}>
                Dit dossier bevat geen debiteuren of cli&euml;nten met geldige adresgegevens. Ga naar
                <Link to={`/file/${props.meta.data.file.id}/party`}>partijen</Link> om dit te verhelpen.
            </MessageBar>
        )
    }

    if (!children?.length) {
        return (
            <Alert
                intent="info"
                action={
                    props.isEditable && {
                        icon: <AddRegular />,
                        onClick: addCallback,
                        children: 'Toevoegen',
                    }
                }
            >
                Deze gesplitste declaratie bevat geen cliënten
            </Alert>
        )
    }

    return (
        <Tree
            size="medium"
            aria-label="Clienten"
            appearance="transparent"
            className={css(baseListStyles.listContainer)}
            onDragOver={event => {
                // Allow the whole tree to be dragged over.
                event.preventDefault()

                const currentElement = elements.current[currentPosition]
                const nextElement = elements.current.find((element: HTMLElement, position: number) =>
                    // Only initiate the move when we passed at least half of the next item.
                    position <= currentPosition
                        ? event.clientY < element.offsetTop + (element.offsetHeight + currentElement.offsetHeight) / 2
                        : event.clientY > element.offsetTop + (element.offsetHeight - currentElement.offsetHeight) / 2,
                )

                if (nextElement) {
                    setNextPosition(elements.current.findIndex(element => element === nextElement))
                }
            }}
            onDragEnter={event => {
                // Fix incidental collapses into non dragging state.
                event.preventDefault()
            }}
            onDragEnd={() => {
                setCurrentPosition(undefined)
                setNextPosition(undefined)
            }}
        >
            {props.isEditable && (
                <Toolbar size="small" className={styles.toolbar}>
                    <ToolbarGroup>
                        <Tooltip content="Voeg nieuwe cliënt toe" relationship="description" withArrow>
                            <ToolbarButton icon={<AddRegular />} onClick={addCallback}>
                                Toevoegen
                            </ToolbarButton>
                        </Tooltip>
                    </ToolbarGroup>
                </Toolbar>
            )}
            {children?.map((child: InvoiceMutationDto, index: number) => (
                <>
                    {!child && <></>}
                    {child && (
                        <div key={child.number} ref={element => (elements.current[index] = element)}>
                            <Stack
                                tokens={{ childrenGap: 'm' }}
                                horizontal
                                verticalAlign="center"
                                draggable={props.isEditable}
                                onDragStart={() => props.isEditable && setCurrentPosition(index)}
                                onDragOver={() => props.isEditable && setNextPosition(index)}
                                className={css(
                                    baseListStyles.itemCell,
                                    currentPosition !== undefined && 'is-dragging',
                                    currentPosition === index && 'is-dragged',
                                    nextPosition === index && 'is-selected',
                                    styles.treeItem,
                                )}
                            >
                                <StackItem grow>
                                    <InvoiceChildContent
                                        {...props}
                                        child={child}
                                        index={index}
                                        prefix={`children[${index}]`}
                                        isDragging={currentPosition !== undefined}
                                        filePartyOptions={filePartyOptions}
                                    />
                                </StackItem>
                                {props.isEditable && <ReOrderDotsVerticalRegular style={{ cursor: 'move' }} />}
                            </Stack>
                        </div>
                    )}
                </>
            ))}
        </Tree>
    )
}
